import React, { useState, useEffect } from 'react';
import { Search, Calendar, Clock, Users, CheckCircle, XCircle, Plus, Edit2, Trash2, MapPin, DollarSign, Eye, Printer, Settings } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../api';
import Pagination from '../common/Pagination';
import EmptyState from '../common/EmptyState';
import Modal from '../Modal';
import FormInput from '../FormInput';
import ConfirmationModal from '../ConfirmationModal';

const SessionsTab = () => {
  const [sessions, setSessions] = useState([]);
  const [typesConcours, setTypesConcours] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [pagination, setPagination] = useState(null);

  // États pour les modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [detailSession, setDetailSession] = useState(null);
  const [sessionForStatusChange, setSessionForStatusChange] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [formData, setFormData] = useState({
    nom: '',
    type_concours: '',
    date_ouverture_inscription: '',
    date_fermeture_inscription: '',
    date_concours: '',
    date_publication_resultats: '',
    nombre_places_disponibles: '',
    frais_inscription: '',
    lieu_concours: '',
    instructions: '',
    documents_requis: ''
  });
  const [formErrors, setFormErrors] = useState({});

  // États pour la confirmation de suppression
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [confirmConfig, setConfirmConfig] = useState({});

  // Statuts disponibles avec leurs descriptions
  const statusOptions = [
    { value: 'PREPARATION', label: 'Préparation', description: 'Session en cours de préparation' },
    { value: 'INSCRIPTION_OUVERTE', label: 'Inscriptions ouvertes', description: 'Les candidats peuvent s\'inscrire' },
    { value: 'INSCRIPTION_FERMEE', label: 'Inscriptions fermées', description: 'Les inscriptions sont fermées' },
    { value: 'EN_COURS', label: 'En cours', description: 'Le concours est en cours' },
    { value: 'CORRECTION', label: 'Correction', description: 'Correction des épreuves en cours' },
    { value: 'TERMINE', label: 'Terminé', description: 'Session terminée avec résultats publiés' },
    { value: 'ANNULE', label: 'Annulé', description: 'Session annulée' }
  ];

  const fetchSessions = async () => {
    setLoading(true);
    setError('');
    
    try {
      const queryParams = new URLSearchParams({
        page: currentPage,
        page_size: pageSize
      });
      
      if (searchTerm.trim()) {
        queryParams.append('search', searchTerm.trim());
      }
      
      const response = await api.get(`/concours/sessions/?${queryParams}`);
      
      if (response.data) {
        const responseData = response.data.data || response.data.results || [];
        setSessions(Array.isArray(responseData) ? responseData : []);
        setPagination(response.data.pagination || {
          count: response.data.count || 0,
          total_pages: Math.ceil((response.data.count || 0) / pageSize),
          current_page: currentPage,
          page_size: pageSize
        });
      }
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      setError('Erreur lors du chargement des sessions');
      setSessions([]);
      setPagination(null);
      toast.error('Impossible de charger les sessions');
    } finally {
      setLoading(false);
    }
  };

  const fetchTypesConcours = async () => {
    try {
      const response = await api.get('/concours/types-concours/');
      const responseData = response.data.data || response.data.results || [];
      setTypesConcours(Array.isArray(responseData) ? responseData : []);
    } catch (error) {
      console.error('Erreur lors du chargement des types:', error);
      setTypesConcours([]);
    }
  };

  useEffect(() => {
    fetchTypesConcours();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchSessions();
    }, searchTerm ? 300 : 0);

    return () => clearTimeout(timeoutId);
  }, [currentPage, pageSize, searchTerm]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // Gestion du formulaire
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear l'erreur si l'utilisateur corrige le champ
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Ouvrir le modal de création/modification
  const handleOpenModal = (session = null) => {
    if (session) {
      setSelectedSession(session);
      setFormData({
        nom: session.nom || '',
        type_concours: session.type_concours?.id || '',
        date_ouverture_inscription: session.date_ouverture_inscription || '',
        date_fermeture_inscription: session.date_fermeture_inscription || '',
        date_concours: session.date_concours || '',
        date_publication_resultats: session.date_publication_resultats || '',
        nombre_places_disponibles: session.nombre_places_disponibles || '',
        frais_inscription: session.frais_inscription || '',
        lieu_concours: session.lieu_concours || '',
        instructions: session.instructions || '',
        documents_requis: session.documents_requis || ''
      });
    } else {
      setSelectedSession(null);
      setFormData({
        nom: '',
        type_concours: '',
        date_ouverture_inscription: '',
        date_fermeture_inscription: '',
        date_concours: '',
        date_publication_resultats: '',
        nombre_places_disponibles: '',
        frais_inscription: '',
        lieu_concours: '',
        instructions: '',
        documents_requis: ''
      });
    }
    setFormErrors({});
    setIsModalOpen(true);
  };

  // Ouvrir le modal de détails
  const handleOpenDetailModal = (session) => {
    setDetailSession(session);
    setIsDetailModalOpen(true);
  };

  // Fonction d'impression
  const handlePrint = () => {
    window.print();
  };

  // Gestion du changement de statut
  const handleOpenStatusModal = (session) => {
    setSessionForStatusChange(session);
    setNewStatus(session.statut);
    setIsStatusModalOpen(true);
  };

  const handleStatusChange = async () => {
    if (!sessionForStatusChange || !newStatus) return;
    
    setChangingStatus(true);
    try {
      await api.patch(`/concours/sessions/${sessionForStatusChange.id}/`, {
        statut: newStatus
      });
      
      toast.success(`Statut de la session "${sessionForStatusChange.nom}" mis à jour avec succès`);
      fetchSessions();
      setIsStatusModalOpen(false);
    } catch (error) {
      console.error("Erreur lors du changement de statut:", error);
      
      let errorMsg = "Erreur lors du changement de statut";
      if (error.response && error.response.data) {
        const responseData = error.response.data;
        if (typeof responseData === 'object') {
          const errorMessages = Object.entries(responseData)
            .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
            .join('\n');
          errorMsg = errorMessages || errorMsg;
        } else if (typeof responseData === 'string') {
          errorMsg = responseData;
        }
      }
      toast.error(errorMsg);
    } finally {
      setChangingStatus(false);
    }
  };

  // Validation du formulaire
  const validateForm = () => {
    const errors = {};
    
    if (!formData.nom.trim()) {
      errors.nom = "Le nom est requis";
    }
    if (!formData.type_concours) {
      errors.type_concours = "Le type de concours est requis";
    }
    if (!formData.date_ouverture_inscription) {
      errors.date_ouverture_inscription = "La date d'ouverture est requise";
    }
    if (!formData.date_fermeture_inscription) {
      errors.date_fermeture_inscription = "La date de fermeture est requise";
    }
    if (!formData.date_concours) {
      errors.date_concours = "La date du concours est requise";
    }
    if (!formData.nombre_places_disponibles) {
      errors.nombre_places_disponibles = "Le nombre de places est requis";
    } else if (isNaN(formData.nombre_places_disponibles) || formData.nombre_places_disponibles <= 0) {
      errors.nombre_places_disponibles = "Le nombre de places doit être un nombre positif";
    }

    // Validation des dates
    if (formData.date_ouverture_inscription && formData.date_fermeture_inscription) {
      if (new Date(formData.date_ouverture_inscription) >= new Date(formData.date_fermeture_inscription)) {
        errors.date_fermeture_inscription = "La date de fermeture doit être après la date d'ouverture";
      }
    }
    if (formData.date_fermeture_inscription && formData.date_concours) {
      if (new Date(formData.date_fermeture_inscription) >= new Date(formData.date_concours)) {
        errors.date_concours = "La date du concours doit être après la fermeture des inscriptions";
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Soumettre le formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const dataToSend = {
        ...formData,
        nombre_places_disponibles: parseInt(formData.nombre_places_disponibles),
        frais_inscription: formData.frais_inscription ? parseFloat(formData.frais_inscription) : 0
      };

      if (selectedSession) {
        // Mise à jour
        await api.put(`/concours/sessions/${selectedSession.id}/`, dataToSend);
        toast.success("Session mise à jour avec succès");
      } else {
        // Création
        await api.post('/concours/sessions/', dataToSend);
        toast.success("Session créée avec succès");
      }

      fetchSessions();
      setIsModalOpen(false);
    } catch (error) {
      console.error("Erreur lors de l'opération:", error);
      
      let errorMsg = "Une erreur est survenue. Veuillez réessayer.";
      if (error.response && error.response.data) {
        const responseData = error.response.data;
        if (typeof responseData === 'object') {
          const errorMessages = Object.entries(responseData)
            .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
            .join('\n');
          errorMsg = errorMessages || errorMsg;
        } else if (typeof responseData === 'string') {
          errorMsg = responseData;
        }
      }
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  // Gestion de la suppression
  const handleOpenDeleteModal = (session) => {
    setSessionToDelete(session);
    setConfirmConfig({
      title: "Supprimer la session",
      message: `Êtes-vous sûr de vouloir supprimer la session "${session.nom}" ?`,
      confirmText: "Supprimer",
      type: "danger"
    });
    setIsConfirmModalOpen(true);
  };

  const handleDelete = async () => {
    if (!sessionToDelete) return;
    
    setDeleteLoading(true);
    try {
      await api.delete(`/concours/sessions/${sessionToDelete.id}/`);
      toast.success("Session supprimée avec succès");
      fetchSessions();
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      toast.error("Impossible de supprimer cette session. Veuillez réessayer.");
    } finally {
      setDeleteLoading(false);
      setIsConfirmModalOpen(false);
    }
  };

  const getStatusBadge = (statut) => {
    const statusConfig = {
      'PREPARATION': { color: 'bg-blue-100 text-blue-800', icon: Clock, label: 'Préparation' },
      'INSCRIPTION_OUVERTE': { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Inscriptions ouvertes' },
      'INSCRIPTION_FERMEE': { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Inscriptions fermées' },
      'EN_COURS': { color: 'bg-orange-100 text-orange-800', icon: Clock, label: 'En cours' },
      'CORRECTION': { color: 'bg-purple-100 text-purple-800', icon: Clock, label: 'Correction' },
      'TERMINE': { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Terminé' },
      'ANNULE': { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Annulé' }
    };
    
    const config = statusConfig[statut] || { color: 'bg-gray-100 text-gray-800', icon: Clock, label: statut };
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </span>
    );
  };

  // Rendering du footer du modal de création/modification
  const renderModalFooter = () => (
    <div className="flex justify-end space-x-4">
      <button
        type="button"
        className="inline-flex justify-center px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        onClick={() => setIsModalOpen(false)}
      >
        Annuler
      </button>
      <button
        type="button"
        className={`inline-flex justify-center px-6 py-2.5 text-sm font-medium text-white bg-[#202654] border border-transparent rounded-md shadow-sm hover:bg-[#161c3f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] ${submitting ? 'opacity-75 cursor-not-allowed' : ''}`}
        onClick={handleSubmit}
        disabled={submitting}
      >
        {submitting ? (
          <>
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Traitement...
          </>
        ) : (
          selectedSession ? 'Mettre à jour' : 'Créer'
        )}
      </button>
    </div>
  );

  // Rendering du footer du modal de détails
  const renderDetailModalFooter = () => (
    <div className="flex justify-between">
      <button
        type="button"
        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        onClick={handlePrint}
      >
        <Printer className="h-4 w-4 mr-2" />
        Imprimer
      </button>
      <button
        type="button"
        className="inline-flex justify-center px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        onClick={() => setIsDetailModalOpen(false)}
      >
        Fermer
      </button>
    </div>
  );

  if (loading && sessions.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            type="text"
            placeholder="Rechercher une session..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#202654] hover:bg-[#202654]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654]"
        >
          <Plus className="h-5 w-5 mr-2" />
          Nouvelle Session
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {sessions.length === 0 && !loading ? (
        <EmptyState
          icon={Calendar}
          title="Aucune session"
          description="Aucune session de concours trouvée."
          actionLabel="Créer une session"
          onAction={() => handleOpenModal()}
        />
      ) : (
        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Session
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date concours
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Places
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sessions.map((session) => (
                  <tr key={session.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                            <Calendar className="h-5 w-5 text-purple-600" />
                          </div>
                        </div>
                        <div className="ml-4 min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-900 truncate">{session.nom}</div>
                          {session.lieu_concours && (
                            <div className="text-sm text-gray-500 flex items-center">
                              <MapPin className="h-3 w-3 mr-1 flex-shrink-0" />
                              <span className="truncate">{session.lieu_concours}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 truncate max-w-xs">
                        {session.type_concours?.nom || 'Non défini'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {session.date_concours ? new Date(session.date_concours).toLocaleDateString('fr-FR') : 'Non définie'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900">
                        <Users className="h-4 w-4 mr-1 text-gray-400" />
                        {session.nombre_places_disponibles || 0}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(session.statut)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleOpenDetailModal(session)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded-full hover:bg-blue-50 transition-colors"
                          title="Voir les détails"
                        >
                          <Eye className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleOpenStatusModal(session)}
                          className="text-purple-600 hover:text-purple-900 p-1 rounded-full hover:bg-purple-50 transition-colors"
                          title="Changer le statut"
                        >
                          <Settings className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleOpenModal(session)}
                          className="text-indigo-600 hover:text-indigo-900 p-1 rounded-full hover:bg-indigo-50 transition-colors"
                          title="Modifier"
                        >
                          <Edit2 className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleOpenDeleteModal(session)}
                          className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-50 transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination && pagination.total_pages > 1 && (
            <div className="bg-white px-4 py-3 border-t border-gray-200">
              <Pagination
                currentPage={currentPage}
                totalPages={pagination.total_pages}
                pageSize={pageSize}
                totalCount={pagination.count}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          )}
        </div>
      )}

      {/* Modal de création/modification */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedSession ? 'Modifier la session' : 'Nouvelle session'}
        footer={renderModalFooter()}
        maxWidth="max-w-4xl"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormInput
              label="Nom de la session"
              type="text"
              name="nom"
              value={formData.nom}
              onChange={handleInputChange}
              placeholder="Ex: Session 2024 Licence 1"
              required
              error={formErrors.nom}
              icon={Calendar}
            />
            
            <FormInput
              label="Type de concours"
              type="select"
              name="type_concours"
              value={formData.type_concours}
              onChange={handleInputChange}
              options={[
                { value: '', label: 'Sélectionner un type' },
                ...typesConcours.map(type => ({ value: type.id, label: type.nom }))
              ]}
              required
              error={formErrors.type_concours}
            />

            <FormInput
              label="Date d'ouverture des inscriptions"
              type="date"
              name="date_ouverture_inscription"
              value={formData.date_ouverture_inscription}
              onChange={handleInputChange}
              required
              error={formErrors.date_ouverture_inscription}
            />

            <FormInput
              label="Date de fermeture des inscriptions"
              type="date"
              name="date_fermeture_inscription"
              value={formData.date_fermeture_inscription}
              onChange={handleInputChange}
              required
              error={formErrors.date_fermeture_inscription}
            />

            <FormInput
              label="Date du concours"
              type="date"
              name="date_concours"
              value={formData.date_concours}
              onChange={handleInputChange}
              required
              error={formErrors.date_concours}
            />

            <FormInput
              label="Date de publication des résultats"
              type="date"
              name="date_publication_resultats"
              value={formData.date_publication_resultats}
              onChange={handleInputChange}
              error={formErrors.date_publication_resultats}
            />

            <FormInput
              label="Nombre de places disponibles"
              type="number"
              name="nombre_places_disponibles"
              value={formData.nombre_places_disponibles}
              onChange={handleInputChange}
              placeholder="Ex: 50"
              required
              error={formErrors.nombre_places_disponibles}
              icon={Users}
            />

            <FormInput
              label="Frais d'inscription (FCFA)"
              type="number"
              name="frais_inscription"
              value={formData.frais_inscription}
              onChange={handleInputChange}
              placeholder="Ex: 25000"
              error={formErrors.frais_inscription}
              icon={DollarSign}
            />
          </div>

          <FormInput
            label="Lieu du concours"
            type="text"
            name="lieu_concours"
            value={formData.lieu_concours}
            onChange={handleInputChange}
            placeholder="Ex: Campus principal, Amphithéâtre A"
            error={formErrors.lieu_concours}
            icon={MapPin}
          />

          <FormInput
            label="Instructions"
            type="textarea"
            name="instructions"
            value={formData.instructions}
            onChange={handleInputChange}
            placeholder="Instructions spécifiques pour cette session..."
            error={formErrors.instructions}
            rows={3}
          />

          <FormInput
            label="Documents requis"
            type="textarea"
            name="documents_requis"
            value={formData.documents_requis}
            onChange={handleInputChange}
            placeholder="Liste des documents à fournir..."
            error={formErrors.documents_requis}
            rows={3}
          />

          <div className="text-sm text-gray-500 mt-4">
            <span className="text-red-500">*</span> Champs obligatoires
          </div>
        </form>
      </Modal>

      {/* Modal de détails avec impression */}
      <Modal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        title="Détails de la session"
        footer={renderDetailModalFooter()}
        maxWidth="max-w-4xl"
      >
        {detailSession && (
          <div className="space-y-6 print:space-y-4">
            {/* En-tête pour l'impression */}
            <div className="hidden print:block text-center mb-8">
              <h1 className="text-2xl font-bold text-gray-900">Détails de la Session de Concours</h1>
              <p className="text-gray-600 mt-2">Généré le {new Date().toLocaleDateString('fr-FR')}</p>
            </div>

            {/* Informations générales */}
            <div className="bg-gray-50 p-6 rounded-lg print:bg-white print:border print:border-gray-300">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Calendar className="h-5 w-5 mr-2 text-purple-600" />
                Informations générales
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nom de la session</label>
                  <p className="mt-1 text-sm text-gray-900">{detailSession.nom}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Type de concours</label>
                  <p className="mt-1 text-sm text-gray-900">{detailSession.type_concours?.nom || 'Non défini'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Statut</label>
                  <div className="mt-1">{getStatusBadge(detailSession.statut)}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nombre de places</label>
                  <p className="mt-1 text-sm text-gray-900 flex items-center">
                    <Users className="h-4 w-4 mr-1 text-gray-400" />
                    {detailSession.nombre_places_disponibles || 0} places
                  </p>
                </div>
              </div>
            </div>

            {/* Dates importantes */}
            <div className="bg-blue-50 p-6 rounded-lg print:bg-white print:border print:border-gray-300">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Clock className="h-5 w-5 mr-2 text-blue-600" />
                Calendrier
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Ouverture des inscriptions</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {detailSession.date_ouverture_inscription ? 
                      new Date(detailSession.date_ouverture_inscription).toLocaleDateString('fr-FR') : 
                      'Non définie'
                    }
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Fermeture des inscriptions</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {detailSession.date_fermeture_inscription ? 
                      new Date(detailSession.date_fermeture_inscription).toLocaleDateString('fr-FR') : 
                      'Non définie'
                    }
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Date du concours</label>
                  <p className="mt-1 text-sm text-gray-900 font-semibold">
                    {detailSession.date_concours ? 
                      new Date(detailSession.date_concours).toLocaleDateString('fr-FR') : 
                      'Non définie'
                    }
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Publication des résultats</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {detailSession.date_publication_resultats ? 
                      new Date(detailSession.date_publication_resultats).toLocaleDateString('fr-FR') : 
                      'Non définie'
                    }
                  </p>
                </div>
              </div>
            </div>

            {/* Informations pratiques */}
            <div className="bg-green-50 p-6 rounded-lg print:bg-white print:border print:border-gray-300">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <MapPin className="h-5 w-5 mr-2 text-green-600" />
                Informations pratiques
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Lieu du concours</label>
                  <p className="mt-1 text-sm text-gray-900">{detailSession.lieu_concours || 'Non défini'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Frais d'inscription</label>
                  <p className="mt-1 text-sm text-gray-900 flex items-center">
                    <DollarSign className="h-4 w-4 mr-1 text-gray-400" />
                    {detailSession.frais_inscription ? 
                      `${detailSession.frais_inscription.toLocaleString()} FCFA` : 
                      'Gratuit'
                    }
                  </p>
                </div>
              </div>
            </div>

            {/* Instructions */}
            {detailSession.instructions && (
              <div className="bg-yellow-50 p-6 rounded-lg print:bg-white print:border print:border-gray-300">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Instructions</h3>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{detailSession.instructions}</p>
              </div>
            )}

            {/* Documents requis */}
            {detailSession.documents_requis && (
              <div className="bg-orange-50 p-6 rounded-lg print:bg-white print:border print:border-gray-300">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Documents requis</h3>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{detailSession.documents_requis}</p>
              </div>
            )}

            {/* Métadonnées */}
            <div className="border-t pt-6 print:border-gray-300">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-500">
                <div>
                  <label className="block font-medium">Créé le</label>
                  <p>{detailSession.created_at ? new Date(detailSession.created_at).toLocaleString('fr-FR') : 'Non disponible'}</p>
                </div>
                <div>
                  <label className="block font-medium">Dernière modification</label>
                  <p>{detailSession.updated_at ? new Date(detailSession.updated_at).toLocaleString('fr-FR') : 'Non disponible'}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de changement de statut */}
      <Modal
        isOpen={isStatusModalOpen}
        onClose={() => setIsStatusModalOpen(false)}
        title="Changer le statut de la session"
        maxWidth="max-w-2xl"
      >
        {sessionForStatusChange && (
          <div className="space-y-6">
            {/* Informations de la session */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {sessionForStatusChange.nom}
              </h3>
              <p className="text-sm text-gray-600">
                Type: {sessionForStatusChange.type_concours?.nom || 'Non défini'}
              </p>
              <div className="mt-2">
                <span className="text-sm text-gray-600">Statut actuel: </span>
                {getStatusBadge(sessionForStatusChange.statut)}
              </div>
            </div>

            {/* Sélection du nouveau statut */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Nouveau statut
              </label>
              <div className="space-y-3">
                {statusOptions.map((option) => (
                  <div
                    key={option.value}
                    className={`relative flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${
                      newStatus === option.value
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                    onClick={() => setNewStatus(option.value)}
                  >
                    <div className="flex items-center h-5">
                      <input
                        type="radio"
                        name="status"
                        value={option.value}
                        checked={newStatus === option.value}
                        onChange={() => setNewStatus(option.value)}
                        className="h-4 w-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                      />
                    </div>
                    <div className="ml-3 flex-1">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-gray-900 cursor-pointer">
                          {option.label}
                        </label>
                        {getStatusBadge(option.value)}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        {option.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Avertissement si changement critique */}
            {(newStatus === 'ANNULE' || newStatus === 'TERMINE') && newStatus !== sessionForStatusChange.statut && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <XCircle className="h-5 w-5 text-yellow-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      Attention
                    </h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p>
                        {newStatus === 'ANNULE' 
                          ? 'L\'annulation d\'une session peut avoir des conséquences importantes sur les candidats inscrits.'
                          : 'Marquer une session comme terminée indique que tous les processus sont finalisés.'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                onClick={() => setIsStatusModalOpen(false)}
              >
                Annuler
              </button>
              <button
                type="button"
                className={`inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md shadow-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 ${
                  changingStatus || newStatus === sessionForStatusChange.statut ? 'opacity-75 cursor-not-allowed' : ''
                }`}
                onClick={handleStatusChange}
                disabled={changingStatus || newStatus === sessionForStatusChange.statut}
              >
                {changingStatus ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Changement...
                  </>
                ) : (
                  'Changer le statut'
                )}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de confirmation de suppression */}
      <ConfirmationModal 
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleDelete}
        title={confirmConfig.title || "Supprimer la session"}
        message={confirmConfig.message || "Êtes-vous sûr de vouloir supprimer cette session ?"}
        confirmText={confirmConfig.confirmText || "Supprimer"}
        cancelText="Annuler"
        type={confirmConfig.type || "danger"}
        isLoading={deleteLoading}
      />
    </div>
  );
};

export default SessionsTab; 